<template>
	<MultiselectDesktop
		v-if="!isMobile"
		v-bind="desktopProps"
		v-model="multiselectValue"
		@open="emit('open')"
		@close="emit('close')"
		@searchChange="(query) => emit('searchChange', query)"
		:canClear="canClear"
	/>
	<MultiselectProxyInput
		v-else
		v-bind="proxyProps"
		@click="openMultiselectModal"
		@clear="handleClear"
		@remove-item="handleRemoveItem"
	/>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useMobile } from '@composables/useMobile';
import { useMobileModal } from '@composables/useMobileModal';
import MultiselectDesktop from '@components/Multiselect/MultiselectDesktop.vue';
import MultiselectMobile from '@components/Multiselect/MultiselectMobile.vue';
import MultiselectProxyInput from '@components/Multiselect/MultiselectProxyInput.vue';
import { multiselectEmitsDefinition, multiselectPropsDefinition } from '@app/interfaces/useMultiselect.interface';
import { MultiselectMode } from '@app/enums/MultiselectMode';

const props = defineProps(multiselectPropsDefinition);

const emit = defineEmits(multiselectEmitsDefinition);

const { isMobile } = useMobile();
const { open: openMobileModal } = useMobileModal();

const multiselectValue = ref(props.modelValue);
const mobileModalProps = ref<Record<string, any> | null>(null);

const desktopProps = computed(() => ({
	options: props.options,
	mode: props.mode,
	placeholder: props.placeholder,
	alwaysShowPlaceholder: props.alwaysShowPlaceholder,
	noResultsText: props.noResultsText,
	showOptionsOnSearchOnly: props.showOptionsOnSearchOnly,
	loading: props.loading,
	startTypingText: props.startTypingText,
	//in Desktop Mode we don't show selected items at all in the dropdown list
	sortSelectedToTop: false,
	description: props.description,
	warning: props.warning
}));

const proxyProps = computed(() => ({
	multiselectValue: multiselectValue.value,
	options: props.options,
	mode: props.mode,
	placeholder: props.placeholder,
	alwaysShowPlaceholder: props.alwaysShowPlaceholder,
	startTypingText: props.startTypingText,
	description: props.description,
	warning: props.warning
}));

const handleMobileUpdate = (value: string | number | (string | number)[] | null): void => {
	multiselectValue.value = value;
};

const handleMobileClose = (): void => {
	mobileModalProps.value = null;
	emit('close');
};

const handleChangeContinentClick = (): void => {
	emit('changeContinent');
};

const getInitialValue = (): string | number | (string | number)[] | null => {
	// For TAGS mode, preserve the current selection; for SINGLE mode, start fresh
	return props.mode === MultiselectMode.TAGS ? multiselectValue.value : null;
};

const openMultiselectModal = (): void => {
	if (props.disabled) return;

	mobileModalProps.value = reactive({
		modelValue: getInitialValue(),
		options: props.options,
		mode: props.mode,
		maxHeight: props.maxHeight,
		placeholder: props.placeholder,
		alwaysShowPlaceholder: props.alwaysShowPlaceholder,
		disabled: props.disabled,
		searchable: props.searchable,
		canClear: props.canClear,
		noOptionsText: props.noOptionsText,
		noResultsText: props.noResultsText,
		showOptionsOnSearchOnly: props.showOptionsOnSearchOnly,
		loading: props.loading,
		sortSelectedToTop: props.sortSelectedToTop,
		description: props.description,
		warning: props.warning,
		selectedContinentName: computed(() => props.selectedContinentName),
		footer: props.footer,
		onUpdateValues: handleMobileUpdate,
		onClose: handleMobileClose,
		onSearchChange: (query: string) => emit('searchChange', query),
		onChangeContinent: handleChangeContinentClick,
	});

	openMobileModal(MultiselectMobile, mobileModalProps.value);
	emit('open');
};

const handleClear = (): void => {
	multiselectValue.value = null;
};

const handleRemoveItem = (value: string | number): void => {
	if (Array.isArray(multiselectValue.value)) {
		multiselectValue.value = multiselectValue.value.filter((v: string | number) => v !== value);
	} else if (multiselectValue.value === value) {
		multiselectValue.value = null;
	}
};

watch(() => props.modelValue, (val) => {
	multiselectValue.value = val;
}, { deep: true });

watch(multiselectValue, (val) => {
	emit('updateValues', val);
}, { deep: true });

watch(() => props.options, (nextOptions) => {
	if (mobileModalProps.value) {
		mobileModalProps.value.options = nextOptions;
	}
}, { deep: true });

watch(() => props.loading, (nextLoading) => {
	if (mobileModalProps.value) {
		mobileModalProps.value.loading = nextLoading;
	}
});
</script>
